import React, { Suspense, lazy, useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import Loader from './Components/Loader/Loader'
import Footer from './Components/Footer/Footer'

import './Components/ColorVariable.css'
import './App.css'
import ScrollToTop from './Components/ScrollToTop'
import { AuthProvider } from './Components/OurStayFilterSection/AuthContext'
import { MasterDataProvider } from './Components/OurStayFilterSection/MasterDataContext'
import { CurrencyProvider } from './Components/OurStayFilterSection/CurrencyContext'

import LoginRegisterModal from './Components/OurStayFilterSection/LoginRegisterModal'

const LandingPage  = lazy(() => import('./Components/LandingPage/LandingPage'))
const Contact      = lazy(() => import('./Components/ContactPage/Contact'))
const About        = lazy(() => import('./Components/AboutPage/About'))
const Sitemap      = lazy(() => import('./Components/OurStayFilterSection/Sitemap'))
const Dashboard    = lazy(() => import('./Components/OurStayFilterSection/Dashboard'))
const NotFound     = lazy(() => import('./Components/Common/NotFound'))
const StaysList    = lazy(() => import('./Components/OurStayFilterSection/StaysList'))
const StayDetails  = lazy(() => import('./Components/CommonStayFilterPart/Staydetailsroute'))
const TermsConditions = lazy(() => import('./Components/Legal/TermsConditions'))
const PrivacyPolicy   = lazy(() => import('./Components/Legal/PrivacyPolicy'))
const CareersPage     = lazy(() => import('./Components/Careers/CareersPage'))
const Testimonials    = lazy(() => import('./Components/Testimonials/TestimonialsPage'))

function GlobalLoginModal() {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);
    window.addEventListener('open-login-modal', handleOpen);
    window.addEventListener('close-login-modal', handleClose);
    window.addEventListener('user-login', handleClose);
    return () => {
      window.removeEventListener('open-login-modal', handleOpen);
      window.removeEventListener('close-login-modal', handleClose);
      window.removeEventListener('user-login', handleClose);
    };
  }, []);

  return <LoginRegisterModal isOpen={isOpen} onClose={() => setIsOpen(false)} />;
}


// Minimum time the loader must be visible (ms).
// Gives the video at least one loop and prevents flash-of-white.
const MIN_LOADER_MS = 2000;

/* Pages where the Footer should NOT appear */
const NO_FOOTER_PATHS = [
  '/hotels',
  '/hotel',
  '/terms',
  '/terms-and-conditions',
  '/privacy',
  '/privacy-policy',
];

function FooterConditional() {
  const { pathname } = useLocation();
  // Hide on exact matches AND on /hotels/:slug (hotel detail, stay detail, booking review)
  const hide =
    NO_FOOTER_PATHS.includes(pathname) ||
    pathname.startsWith('/hotels/');
  if (hide) return null;
  return <Footer />;
}

function App() {
  const [appReady, setAppReady] = useState(false);

  // Mark app as ready once the document has fully loaded
  useEffect(() => {
    if (document.readyState === 'complete') {
      setAppReady(true);
    } else {
      const onLoad = () => setAppReady(true);
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <GlobalLoginModal />
          <CurrencyProvider>
            <MasterDataProvider>
            <ScrollToTop />
            {/* Loader shows until appReady=true AND minDisplayMs elapsed */}
            <Loader isReady={appReady} minDisplayMs={MIN_LOADER_MS} />
            <div style={{ position: "relative", zIndex: 10 }}>
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/"               element={<LandingPage />} />
                  <Route path="/hotels"         element={<StaysList />} />
                  <Route path="/hotel"          element={<StaysList />} />
                  <Route path="/hotels/:hotelSlug" element={<StayDetails />} />
                  <Route path="/contact"        element={<Contact />} />
                  <Route path="/about"          element={<About />} />
                  <Route path="/terms"          element={<TermsConditions />} />
                  <Route path="/terms-and-conditions" element={<TermsConditions />} />
                  <Route path="/privacy"        element={<PrivacyPolicy />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/careers"        element={<CareersPage />} />
                  <Route path="/testimonials"   element={<Testimonials />} />

                  <Route path="/login"          element={<LoginRegisterModal isOpen={true} onClose={() => window.history.back()} />} />
                  <Route path="/sitemap"        element={<Sitemap onBack={() => window.history.back()} />} />
                  <Route
                    path="/dashboard"
                    element={
                      <Dashboard
                        onBack={() => window.history.back()}
                        onHome={() => window.location.assign('/')}
                      />
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </div>
            <FooterConditional />
          </MasterDataProvider>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  )
}

export default App
